/**
 * The laptop calendar's Agenda (2026-10-30, round-2 mockup
 * `AgendaSchedules`): the work in a table, day by day, and the site's
 * repeating schedules beside it.
 *
 * The same model the phone's Today and Schedules tabs draw
 * (`phone-model.ts`, cut into days by `agenda-model.ts`), as a laptop
 * reads it: Time · Type · Where · Robot · Length · Status, one row per
 * task (a robot's run of deliveries is one row that opens), today's
 * finished work folded behind one line, the now line at the site's
 * minute. It fetches nothing; `CalendarLanes` hands it the lanes' data
 * and owns the selection, so a row opens the very panel the Day view
 * opens (`LanePanel`), and a schedule opens `SchedulePanel` in the same
 * place.
 */
import { useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import type { MaintenanceWindow, ScheduleOccurrence, TaskSchedule } from '../../../api/v1';
import { formatZonedClock, formatZonedDateTime } from '../zoned-time';
import {
  blockedTimes,
  buildAgendaDays,
  daySummary,
  earlierText,
  agendaItemMatches,
  needsALook,
  noRunText,
  schedulePaint,
  scheduleSelectionId,
  type AgendaDay,
} from './agenda-model';
import { AGENDA_MAX_DAYS, AGENDA_STEP_DAYS } from './calendar-url';
import { DIDNT_FINISH, paintOf, type LaneItem } from './lane-items';
import { laneVars } from './lane-style';
import { dateKeyOf, type DateKey } from './lane-time';
import { linenRobotKeys } from './robot-groups';
import { useOpenAtNow } from './useOpenAtNow';
import { useRevealOnMount } from './reveal';
import {
  buildScheduleRows,
  burstWords,
  groupMembership,
  isRunning,
  itemsInGroup,
  lengthText,
  nextRunText,
  phoneStatus,
  robotText,
  scheduleMatches,
  whereOf,
  type AgendaRow,
  type CalendarIssue,
  type ScheduleRow,
} from './phone-model';
import type { SiteBar } from './site-row';
import { creatorLabel } from './people';
import { patternBeside, spanInline } from './schedule-span';
import type { LaneData } from './useCalendarLaneData';
import { PriorityBadge } from '../TaskPriority';
import { isHighPriority } from '../../../api/task-priority';
import './AgendaView.css';

export interface AgendaViewProps {
  dateKey: DateKey;
  todayKey: DateKey;
  days: readonly DateKey[];
  timeZone: string;
  nowMs: number;
  data: LaneData;
  /** The toolbar's "Name or floor" box. */
  query: string;
  schedules: readonly TaskSchedule[];
  /** Occurrences to read each schedule's next runs from. */
  occurrences: readonly ScheduleOccurrence[];
  /** How many days ahead `occurrences` reach, for "Not in the next N days". */
  horizonDays: number;
  issues: readonly CalendarIssue[];
  selectedId: string | null;
  onSelect: (id: string, trigger?: HTMLElement | null) => void;
  /** Present while the range can grow. */
  onShowMore?: () => void;
  onOpenDay: (day: DateKey) => void;
  onDeleteMaintenance?: (w: MaintenanceWindow) => void | Promise<boolean>;
  robotLabel: (key: string | null) => string | null;
  /** The floating panel (task or schedule), drawn over the list. */
  panel?: ReactNode;
  /** The signed-in user's subject: a blocked time they booked is "by
   *  you". */
  mySub?: string | null;
}

export function AgendaView(props: AgendaViewProps) {
  const { data, nowMs, timeZone, todayKey } = props;
  const [groupId, setGroupId] = useState<string | null>(null);
  const [needsLook, setNeedsLook] = useState(false);
  const [showEarlier, setShowEarlier] = useState(false);
  const [openBursts, setOpenBursts] = useState<ReadonlySet<string>>(() => new Set());
  const [scheduleQuery, setScheduleQuery] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  useOpenAtNow(scrollRef, { dateKey: props.dateKey, includesToday: props.days.includes(todayKey) });

  const robotsByKey = useMemo(() => new Map(data.robots.map((r) => [r.key, r])), [data.robots]);
  const labelOf = props.robotLabel;
  const linen = useMemo(() => linenRobotKeys(data.robots, data.groups), [data.robots, data.groups]);

  const membership = useMemo(
    () => groupMembership(data.items, data.groups, data.robots),
    [data.items, data.groups, data.robots],
  );
  const activeGroup = groupId && membership.chips.some((c) => c.id === groupId) ? groupId : null;
  const inView = useMemo(
    () =>
      itemsInGroup(data.items, membership, activeGroup).filter((it) =>
        agendaItemMatches(it, props.query, robotsByKey),
      ),
    [data.items, membership, activeGroup, props.query, robotsByKey],
  );
  const issues = props.issues.filter((i) => activeGroup === null || i.groupId === activeGroup);
  const lookItems = inView.filter(needsALook);
  const lookCount = lookItems.length + issues.length;
  const shown = needsLook ? lookItems : inView;

  const days = useMemo(
    () =>
      buildAgendaDays({
        items: shown,
        days: props.days,
        todayKey,
        nowMs,
        timeZone,
        fold: !needsLook,
      }),
    [shown, props.days, todayKey, nowMs, timeZone, needsLook],
  );

  const toggleBurst = (id: string) =>
    setOpenBursts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const rowProps = {
    timeZone,
    labelOf,
    selectedId: props.selectedId,
    onSelect: props.onSelect,
    openBursts,
    onToggleBurst: toggleBurst,
  };

  return (
    <div className="nx-agenda" data-testid="agenda">
      <div className="nx-agenda__main-wrap">
        <div className="nx-agenda__main">
          <div className="nx-agenda__filters" role="group" aria-label="Show">
            <Chip
              on={activeGroup === null}
              onClick={() => setGroupId(null)}
              testId="agenda-chip-all"
            >
              {`All ${data.robots.length} robots`}
            </Chip>
            {membership.chips.length > 1 &&
              membership.chips.map((c) => (
                <Chip
                  key={c.id}
                  on={activeGroup === c.id}
                  onClick={() => setGroupId(c.id)}
                  testId={`agenda-chip-${c.id}`}
                >
                  {c.name}
                </Chip>
              ))}
            <Chip
              on={needsLook}
              onClick={() => setNeedsLook((v) => !v)}
              testId="agenda-chip-look"
              look
            >
              {`Needs a look (${lookCount})`}
            </Chip>
          </div>

          <div className="nx-agenda__scroll" data-testid="agenda-scroll" ref={scrollRef}>
            <div className="nx-agenda__head" aria-hidden="true">
              <span>Time</span>
              <span>Type</span>
              <span>Where</span>
              <span>Robot</span>
              <span>Length</span>
              <span>Status</span>
            </div>

            {needsLook && issues.length > 0 && (
              <section
                className="nx-agenda__issues"
                aria-label="Robot problems now"
                data-testid="agenda-issues"
              >
                <h3 className="nx-agenda__issues-h">Robot problems now · {issues.length}</h3>
                <ul>
                  {issues.map((i) => (
                    <li key={i.id} data-testid={`agenda-issue-${i.id}`}>
                      <span>
                        <b>{i.subject}</b> · {i.words}
                      </span>
                      {i.sinceMs !== null && (
                        <span className="nx-agenda__muted">
                          since {formatZonedClock(i.sinceMs, timeZone)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {days.map((d) => (
              <DaySection
                key={d.key}
                day={d}
                nowMs={nowMs}
                needsLook={needsLook}
                showEarlier={showEarlier}
                onToggleEarlier={() => setShowEarlier((v) => !v)}
                {...rowProps}
              />
            ))}

            <div className="nx-agenda__more">
              {props.onShowMore ? (
                <button
                  type="button"
                  className="nx-lane-btn"
                  onClick={props.onShowMore}
                  data-testid="agenda-show-more"
                >
                  Show next {Math.min(AGENDA_STEP_DAYS, AGENDA_MAX_DAYS - props.days.length)} days
                </button>
              ) : (
                <span className="nx-agenda__muted" data-testid="agenda-max">
                  Showing {props.days.length} days, the most at once. Use › to go further.
                </span>
              )}
              <span className="nx-agenda__muted">
                Automatic charging and returns to dock are not shown.
              </span>
            </div>
          </div>
        </div>
        {props.panel}
      </div>

      <SchedulesAside
        schedules={props.schedules}
        occurrences={props.occurrences}
        nowMs={nowMs}
        timeZone={timeZone}
        horizonDays={props.horizonDays}
        labelOf={labelOf}
        query={scheduleQuery}
        onQuery={setScheduleQuery}
        selectedId={props.selectedId}
        onSelect={props.onSelect}
        blocked={blockedTimes(data.siteBars, nowMs)}
        linen={linen}
        onOpenDay={props.onOpenDay}
        onDeleteMaintenance={props.onDeleteMaintenance}
        mySub={props.mySub ?? null}
      />
    </div>
  );
}

function Chip({
  on,
  onClick,
  children,
  testId,
  look,
}: {
  on: boolean;
  onClick: () => void;
  children: ReactNode;
  testId: string;
  look?: boolean;
}) {
  return (
    <button
      type="button"
      className={`nx-agenda__chip${on ? ' is-on' : ''}${look ? ' is-look' : ''}`}
      aria-pressed={on}
      onClick={onClick}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

// ---- one day ---------------------------------------------------------------------

interface RowCommon {
  timeZone: string;
  labelOf: (key: string | null) => string | null;
  selectedId: string | null;
  onSelect: (id: string, trigger?: HTMLElement | null) => void;
  openBursts: ReadonlySet<string>;
  onToggleBurst: (id: string) => void;
}

function DaySection(
  props: RowCommon & {
    day: AgendaDay;
    nowMs: number;
    needsLook: boolean;
    showEarlier: boolean;
    onToggleEarlier: () => void;
  },
) {
  const { day, nowMs, timeZone } = props;
  const headId = `agenda-day-${day.key}`;
  const hasEarlier = day.earlier.length > 0;
  const before = hasEarlier ? (props.showEarlier ? day.earlier : day.pinned) : [];
  const nowLine = (
    <li
      className="nx-agenda__now"
      aria-label={`Now, ${formatZonedClock(nowMs, timeZone)}`}
      data-testid="agenda-nowline"
    >
      <span>NOW {formatZonedClock(nowMs, timeZone)}</span>
      <span className="nx-agenda__now-rule" aria-hidden="true" />
    </li>
  );
  const empty = day.rows.length === 0 && day.earlier.length === 0;
  return (
    <section
      className="nx-agenda__day"
      aria-labelledby={headId}
      data-testid={`agenda-day-${day.key}`}
    >
      <div className="nx-agenda__day-head">
        <h3 id={headId}>{day.label}</h3>
        <span data-testid={`agenda-day-sum-${day.key}`}>
          {props.needsLook
            ? day.counts.tasks === 0
              ? 'Nothing needs a look'
              : `${day.counts.tasks} didn’t finish`
            : daySummary(day.counts)}
        </span>
      </div>
      {hasEarlier && (
        <div className="nx-agenda__earlier" data-testid={`agenda-earlier-${day.key}`}>
          <span>{earlierText(day.earlierCount)}</span>
          <button
            type="button"
            className="nx-agenda__link"
            aria-expanded={props.showEarlier}
            onClick={props.onToggleEarlier}
            data-testid="agenda-earlier-toggle"
          >
            {props.showEarlier ? 'Hide' : 'Show'}
          </button>
        </div>
      )}
      {empty ? (
        <p className="nx-agenda__empty">
          {props.needsLook ? 'Nothing that day needs a look.' : 'Nothing on the calendar that day.'}
        </p>
      ) : (
        <ul className="nx-agenda__rows" aria-label={day.label}>
          {before.map((r) => (
            <RowView key={r.id} row={r} {...props} />
          ))}
          {day.rows.map((r, i) => (
            <RowWithNow
              key={r.id}
              before={day.nowIndex === i ? nowLine : null}
              row={r}
              {...props}
            />
          ))}
          {day.nowIndex !== null && day.nowIndex === day.rows.length && nowLine}
        </ul>
      )}
    </section>
  );
}

function RowWithNow(props: RowCommon & { before: ReactNode; row: AgendaRow }) {
  return (
    <>
      {props.before}
      <RowView {...props} />
    </>
  );
}

function statusClass(item: LaneItem): string {
  if (DIDNT_FINISH.has(item.outcome)) return ' is-fail';
  if (isRunning(item.outcome)) return ' is-run';
  return '';
}

function TypePill({ item }: { item: LaneItem }) {
  const plan = item.outcome === 'scheduled' || item.outcome === 'queued';
  return (
    <span className={`nx-agenda__pill${plan ? ' is-outline' : ''}`} style={laneVars(paintOf(item))}>
      {item.typeLabel}
    </span>
  );
}

/** A row's muted second words: why it didn't finish, what RMF reports. */
function extraOf(item: LaneItem): string | null {
  if ((DIDNT_FINISH.has(item.outcome) || item.outcome === 'attention') && item.detail) {
    return item.detail;
  }
  return item.waitingNote ?? null;
}

function RowView(props: RowCommon & { row: AgendaRow; nested?: boolean }) {
  const { row, timeZone, labelOf } = props;
  if (row.kind === 'burst') {
    const w = burstWords(row.items);
    const first = row.items[0]!;
    const open = props.openBursts.has(row.id);
    return (
      <li>
        <button
          type="button"
          className={`nx-agenda__row${w.didntFinish ? ' is-fail' : ''}`}
          aria-expanded={open}
          onClick={() => props.onToggleBurst(row.id)}
          data-testid={`agenda-row-${row.id}`}
          data-agenda-id={row.id}
        >
          <span className="nx-agenda__t">{formatZonedClock(row.startMs, timeZone)}</span>
          <span className="nx-agenda__type">
            <TypePill item={first} />
          </span>
          <span className="nx-agenda__where">
            {w.where} <span className="nx-agenda__muted">· {row.items.length} deliveries</span>
          </span>
          <span className="nx-agenda__robot">{robotText(first, labelOf)}</span>
          <span className="nx-agenda__len">{w.length}</span>
          <span className="nx-agenda__status">
            {/* The failure first: a narrow column cuts the end, and "9
                done" is the part that can go. */}
            {w.allFailed ? (
              <span className="is-fail">{w.status}</span>
            ) : w.didntFinish > 0 ? (
              <>
                <span className="is-fail">{w.didntFinish} didn’t finish</span>
                <span className="nx-agenda__muted"> · {w.status}</span>
              </>
            ) : (
              <span className={statusClass(first)}>{w.status}</span>
            )}
            <span className="nx-agenda__caret" aria-hidden="true">
              {open ? '▾' : '▸'}
            </span>
          </span>
        </button>
        {open && (
          <ul className="nx-agenda__burst" aria-label={`${row.items.length} deliveries`}>
            {row.items.map((it) => (
              <RowView key={it.id} {...props} row={{ kind: 'item', id: it.id, item: it }} nested />
            ))}
          </ul>
        )}
      </li>
    );
  }
  const it = row.item;
  const fail = DIDNT_FINISH.has(it.outcome);
  const where = whereOf(it);
  const extra = extraOf(it);
  const selected = props.selectedId === it.id;
  const len = lengthText(it);
  return (
    <li>
      <button
        type="button"
        className={`nx-agenda__row${fail ? ' is-fail' : ''}${it.outcome === 'done' ? ' is-done' : ''}${selected ? ' is-selected' : ''}${props.nested ? ' is-nested' : ''}`}
        aria-pressed={selected}
        onClick={(e) => props.onSelect(it.id, e.currentTarget)}
        data-testid={`agenda-row-${it.id}`}
        data-agenda-id={it.id}
        data-outcome={it.outcome}
      >
        <span className="nx-agenda__t">{formatZonedClock(it.startMs, timeZone)}</span>
        <span className="nx-agenda__type">
          <TypePill item={it} />
        </span>
        <span
          className="nx-agenda__where"
          title={extra ? `${where ?? ''} · ${extra}` : (where ?? undefined)}
        >
          {/* Before the place, not beside the type pill: the type column
              is sized to the longest type name. */}
          {it.high && <PriorityBadge lead testId={`agenda-high-${it.id}`} />}
          {where ?? <span className="nx-agenda__muted">Place not known yet</span>}
          {extra && <span className="nx-agenda__muted"> · {extra}</span>}
        </span>
        <span className="nx-agenda__robot">{robotText(it, labelOf)}</span>
        <span className="nx-agenda__len">{len || '—'}</span>
        <span className="nx-agenda__status">
          <span className={statusClass(it)}>{phoneStatus(it.outcome)}</span>
        </span>
      </button>
    </li>
  );
}

// ---- repeating schedules -----------------------------------------------------------

function SchedulesAside(props: {
  schedules: readonly TaskSchedule[];
  occurrences: readonly ScheduleOccurrence[];
  nowMs: number;
  timeZone: string;
  horizonDays: number;
  labelOf: (key: string | null) => string | null;
  query: string;
  onQuery: (q: string) => void;
  selectedId: string | null;
  onSelect: (id: string, trigger?: HTMLElement | null) => void;
  blocked: SiteBar[];
  linen: ReadonlySet<string>;
  onOpenDay: (day: DateKey) => void;
  onDeleteMaintenance?: (w: MaintenanceWindow) => void | Promise<boolean>;
  mySub: string | null;
}) {
  const { nowMs, timeZone } = props;
  const rows = useMemo(
    () =>
      buildScheduleRows({
        schedules: props.schedules,
        occurrences: props.occurrences,
        nowMs,
        timeZone,
        labelOf: props.labelOf,
        agenda: true,
      }),
    [props.schedules, props.occurrences, nowMs, timeZone, props.labelOf],
  );
  const shown = rows.filter((r) => scheduleMatches(r, props.query));
  const [confirming, setConfirming] = useState<string | null>(null);
  const blockedRef = useRef<HTMLHeadingElement>(null);
  // The Remove whose question is open, drawn again once it goes: Keep
  // sends focus back to it (review BB-B-2, 2026-10-31).
  const removeRef = useRef<HTMLButtonElement>(null);
  const [asked, setAsked] = useState<string | null>(null);
  return (
    <aside
      className="nx-agenda__aside"
      aria-label="Repeating schedules"
      data-testid="agenda-schedules"
    >
      <div className="nx-agenda__aside-top">
        <h3 className="nx-agenda__aside-h">
          {`Repeating schedules (${props.query.trim() ? `${shown.length} of ${rows.length}` : rows.length})`}
        </h3>
        {props.blocked.length > 0 && (
          <button
            type="button"
            className="nx-agenda__link nx-agenda__link--small"
            onClick={() => blockedRef.current?.scrollIntoView({ block: 'nearest' })}
            data-testid="agenda-blocked-jump"
          >
            {`Blocked times (${props.blocked.length}) ↓`}
          </button>
        )}
      </div>
      <input
        type="search"
        className="nx-agenda__search"
        value={props.query}
        onChange={(e) => props.onQuery(e.target.value)}
        placeholder="Search schedules, zones or robots"
        aria-label="Search schedules, zones or robots"
        data-testid="agenda-sched-search"
      />
      {shown.length === 0 ? (
        <p className="nx-agenda__empty">
          {rows.length === 0 ? 'No repeating schedules yet.' : 'No schedule matches that search.'}
        </p>
      ) : (
        <ul className="nx-agenda__cards">
          {shown.map((r) => (
            <li key={r.schedule.schedule_id}>
              <ScheduleCard
                row={r}
                nowMs={nowMs}
                timeZone={timeZone}
                horizonDays={props.horizonDays}
                selected={props.selectedId === scheduleSelectionId(r.schedule.schedule_id)}
                linen={props.linen}
                onSelect={props.onSelect}
              />
            </li>
          ))}
        </ul>
      )}

      <h3 className="nx-agenda__aside-h nx-agenda__aside-h--sub" ref={blockedRef}>
        Blocked times ({props.blocked.length})
      </h3>
      {props.blocked.length === 0 ? (
        <p className="nx-agenda__empty">No blocked time in these days.</p>
      ) : (
        <ul className="nx-agenda__blocks">
          {props.blocked.map((b) => (
            <li key={b.id} className="nx-agenda__block" data-testid={`agenda-block-${b.id}`}>
              <button
                type="button"
                className="nx-agenda__block-open"
                // A lift rule's run opens its panel, which has Skip and Remove:
                // a Remove here could not say whether it meant one run or all.
                onClick={(e) =>
                  b.liftRule
                    ? props.onSelect(b.id, e.currentTarget)
                    : props.onOpenDay(dateKeyOf(b.startMs, timeZone))
                }
                title={b.liftRule ? 'Open this rule' : 'Open that day'}
                data-testid={`agenda-block-open-${b.id}`}
              >
                <span className="nx-agenda__block-reason">
                  {b.liftRule ? b.label : b.maintenance?.reason || 'Blocked'}
                </span>
                <span className="nx-agenda__muted">
                  {formatZonedDateTime(b.startMs, timeZone)} → {formatZonedClock(b.endMs, timeZone)}
                </span>
                {b.maintenance && (
                  <span
                    className="nx-agenda__muted"
                    data-testid={`agenda-block-by-${b.id}`}
                  >
                    Blocked by {creatorLabel(b.maintenance, props.mySub)}
                  </span>
                )}
              </button>
              {props.onDeleteMaintenance && b.maintenance && confirming !== b.id && (
                <button
                  ref={asked === b.id ? removeRef : undefined}
                  type="button"
                  className="nx-agenda__link nx-agenda__link--danger"
                  onClick={() => {
                    setAsked(b.id);
                    setConfirming(b.id);
                  }}
                  data-testid={`agenda-block-remove-${b.id}`}
                >
                  Remove
                </button>
              )}
              {props.onDeleteMaintenance && b.maintenance && confirming === b.id && (
                <AgendaRemoveConfirm
                  back={removeRef}
                  onKeep={() => setConfirming(null)}
                  onRemove={() => {
                    setConfirming(null);
                    props.onDeleteMaintenance!(b.maintenance!);
                  }}
                  testId={`agenda-block-remove-confirm-${b.id}`}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

function ScheduleCard(props: {
  row: ScheduleRow;
  nowMs: number;
  timeZone: string;
  horizonDays: number;
  selected: boolean;
  linen: ReadonlySet<string>;
  onSelect: (id: string, trigger?: HTMLElement | null) => void;
}) {
  const { row: r } = props;
  const id = scheduleSelectionId(r.schedule.schedule_id);
  return (
    <button
      type="button"
      className={`nx-agenda__card${props.selected ? ' is-selected' : ''}`}
      aria-pressed={props.selected}
      onClick={(e) => props.onSelect(id, e.currentTarget)}
      data-testid={`agenda-sched-${r.schedule.schedule_id}`}
      data-agenda-id={id}
    >
      <span className="nx-agenda__card-top">
        <span className="nx-agenda__card-name">
          <span
            className="nx-agenda__swatch"
            style={laneVars(schedulePaint(r, props.linen))}
            aria-hidden="true"
          />
          {r.schedule.name}
          {isHighPriority(r.schedule.task?.priority) && (
            <PriorityBadge testId={`agenda-sched-high-${r.schedule.schedule_id}`} />
          )}
        </span>
        <span className={`nx-agenda__state${r.active ? ' is-on' : ' is-paused'}`}>
          {r.active ? 'Active' : 'Paused'}
        </span>
      </span>
      {/* The end and a start still to come said beside the pattern, with
          the weekday: what tells a carry-over ("until Tue 03/Nov") from
          the schedule it came from ("from Wed 04/Nov"), same name. */}
      <span className="nx-agenda__muted">
        {patternBeside(r.pattern, r.span)}
        {r.span && (
          <>
            ,{' '}
            <span
              className="nx-agenda__card-span"
              data-testid={`agenda-sched-span-${r.schedule.schedule_id}`}
            >
              {spanInline(r.span)}
            </span>
          </>
        )}{' '}
        · {r.robots}
      </span>
      <span
        className="nx-agenda__card-next"
        data-testid={`agenda-sched-next-${r.schedule.schedule_id}`}
      >
        {r.active
          ? `Next: ${r.next ? nextRunText(r.next.start_ms, props.nowMs, props.timeZone) : noRunText(props.horizonDays)}`
          : 'Paused: nothing will run'}
      </span>
    </button>
  );
}

/** Keep / Remove it, in place of a blocked time's Remove. Focus goes to
 *  Keep as it opens, so Enter held on Remove keeps it, and back to that
 *  Remove on Keep: the question used to leave focus on the page, and
 *  never moved it to Keep at all (review BB-B-2, 2026-10-31). The same
 *  `useRevealOnMount` the lane panel's question uses. */
function AgendaRemoveConfirm({
  back,
  onKeep,
  onRemove,
  testId,
}: {
  back: RefObject<HTMLButtonElement | null>;
  onKeep: () => void;
  onRemove: () => void;
  testId: string;
}) {
  const ref = useRevealOnMount<HTMLSpanElement>(undefined, back);
  return (
    <span ref={ref} className="nx-agenda__confirm" role="group" aria-label="Confirm remove">
      <button type="button" className="nx-agenda__link" onClick={onKeep}>
        Keep
      </button>
      <button
        type="button"
        className="nx-agenda__link nx-agenda__link--danger"
        onClick={onRemove}
        data-testid={testId}
      >
        Remove it
      </button>
    </span>
  );
}
